/** Утилиты дат для расписания (сетка — Europe/Moscow, подписи зрителя — опционально). */

export const WEEKDAY_NAMES = [
  'Пн',
  'Вт',
  'Ср',
  'Чт',
  'Пт',
  'Сб',
  'Вс',
];

export const WEEKDAY_FULL = [
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
  'воскресенье',
];

export const TEACHER_TZ = 'Europe/Moscow';
export const TEACHER_TZ_SHORT = 'МСК';

export const VIEWER_TZ_STORAGE_KEY = 'diobuch.schedule.viewerTz';
export const VIEWER_TZ_BROWSER = 'browser';

/** Смещение пояса относительно Москвы: «МСК», «МСК+2», «МСК-1». */
export function formatMskOffset(ianaTz, now = new Date()) {
  if (!ianaTz || ianaTz === TEACHER_TZ) return TEACHER_TZ_SHORT;
  try {
    const hours =
      (tzOffsetMinutes(now, ianaTz) - tzOffsetMinutes(now, TEACHER_TZ)) / 60;
    if (hours === 0) return TEACHER_TZ_SHORT;
    const sign = hours > 0 ? '+' : '-';
    const abs = Math.abs(hours);
    const pretty = Number.isInteger(abs) ? String(abs) : String(abs).replace('.', ',');
    return `${TEACHER_TZ_SHORT}${sign}${pretty}`;
  } catch {
    return TEACHER_TZ_SHORT;
  }
}

function tzOffsetMinutes(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - date.getTime()) / 60000);
}

/**
 * Не 400 IANA-зон: это юридические правила, а не «24 часа Земли».
 * Россия целиком + по городу на каждый обитаемый час (UTC−11…+14) и привычные страны.
 */
export const VIEWER_TZ_GROUPS = [
  {
    options: [{ value: VIEWER_TZ_BROWSER, city: 'Как в браузере' }],
  },
  {
    label: 'Россия',
    options: [
      { value: 'Europe/Kaliningrad', city: 'Калининград', short: 'Клг' },
      { value: 'Europe/Moscow', city: 'Москва', short: 'МСК' },
      { value: 'Europe/Samara', city: 'Самара', short: 'Смр' },
      { value: 'Asia/Yekaterinburg', city: 'Екатеринбург', short: 'Екб' },
      { value: 'Asia/Omsk', city: 'Омск', short: 'Омск' },
      { value: 'Asia/Krasnoyarsk', city: 'Красноярск', short: 'Крс' },
      { value: 'Asia/Irkutsk', city: 'Иркутск', short: 'Ирк' },
      { value: 'Asia/Yakutsk', city: 'Якутск', short: 'Як' },
      { value: 'Asia/Vladivostok', city: 'Владивосток', short: 'Влд' },
      { value: 'Asia/Magadan', city: 'Магадан', short: 'Мгд' },
      { value: 'Asia/Kamchatka', city: 'Камчатка', short: 'Кам' },
    ],
  },
  {
    label: 'Другие страны',
    options: [
      { value: 'Pacific/Pago_Pago', city: 'Паго-Паго' },
      { value: 'Pacific/Honolulu', city: 'Гонолулу' },
      { value: 'America/Anchorage', city: 'Анкоридж' },
      { value: 'America/Los_Angeles', city: 'Лос-Анджелес' },
      { value: 'America/Denver', city: 'Денвер' },
      { value: 'America/Chicago', city: 'Чикаго' },
      { value: 'America/New_York', city: 'Нью-Йорк' },
      { value: 'America/Santiago', city: 'Сантьяго' },
      { value: 'America/Argentina/Buenos_Aires', city: 'Буэнос-Айрес' },
      { value: 'America/Noronha', city: 'Норонья' },
      { value: 'Atlantic/Cape_Verde', city: 'Кабо-Верде' },
      { value: 'Atlantic/Reykjavik', city: 'Рейкьявик' },
      { value: 'Europe/London', city: 'Лондон' },
      { value: 'Europe/Paris', city: 'Париж' },
      { value: 'Europe/Berlin', city: 'Берлин' },
      { value: 'Europe/Kyiv', city: 'Киев' },
      { value: 'Africa/Johannesburg', city: 'Йоханнесбург' },
      { value: 'Europe/Minsk', city: 'Минск' },
      { value: 'Europe/Istanbul', city: 'Стамбул' },
      { value: 'Asia/Jerusalem', city: 'Тель-Авив' },
      { value: 'Asia/Dubai', city: 'Дубай' },
      { value: 'Asia/Baku', city: 'Баку' },
      { value: 'Asia/Yerevan', city: 'Ереван' },
      { value: 'Asia/Tbilisi', city: 'Тбилиси' },
      { value: 'Asia/Tashkent', city: 'Ташкент' },
      { value: 'Asia/Almaty', city: 'Алматы' },
      { value: 'Asia/Kolkata', city: 'Дели' },
      { value: 'Asia/Dhaka', city: 'Дакка' },
      { value: 'Asia/Bangkok', city: 'Бангкок' },
      { value: 'Asia/Shanghai', city: 'Пекин' },
      { value: 'Asia/Tokyo', city: 'Токио' },
      { value: 'Australia/Sydney', city: 'Сидней' },
      { value: 'Pacific/Auckland', city: 'Окленд' },
      { value: 'Pacific/Tongatapu', city: 'Тонга' },
      { value: 'Pacific/Kiritimati', city: 'Киритимати' },
    ],
  },
];

export const VIEWER_TZ_OPTIONS = VIEWER_TZ_GROUPS.flatMap((g) => g.options);

export function formatViewerTzOptionLabel(option, { now = new Date(), browserTz } = {}) {
  if (option.value === VIEWER_TZ_BROWSER) {
    if (browserTz && browserTz !== TEACHER_TZ) {
      return `${option.city} (${formatMskOffset(browserTz, now)})`;
    }
    return option.city;
  }
  return `${option.city} (${formatMskOffset(option.value, now)})`;
}

export function moscowDateISO(d = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TEACHER_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** 0 = понедельник … 6 = воскресенье, по календарю МСК. */
export function moscowWeekday(d = new Date()) {
  const iso = typeof d === 'string' ? d : moscowDateISO(d);
  return (parseISODate(iso).getDay() + 6) % 7;
}

export function parseISODate(iso) {
  const [y, m, day] = iso.split('-').map(Number);
  return new Date(y, m - 1, day);
}

export function addDaysISO(iso, days) {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + days);
  return formatLocalISO(d);
}

export function formatLocalISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function mondayOf(iso) {
  const d = parseISODate(iso);
  const wd = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - wd);
  return formatLocalISO(d);
}

export function weekDates(mondayIso) {
  return Array.from({ length: 7 }, (_, i) => addDaysISO(mondayIso, i));
}

export function formatTimeFromISO(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TEACHER_TZ,
  });
}

export function formatDateShort(isoDate) {
  const d = parseISODate(isoDate);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export function minutesFromMidnight(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Расширить рабочее окно, чтобы отрезки учеников вне окна группы оставались на сетке.
 * @param {string} workStart HH:MM
 * @param {string} workEnd HH:MM
 * @param {Array<{start?: string, duration?: number}>} segments
 */
export function scheduleGridExtent(workStart, workEnd, segments = []) {
  let startMin = minutesFromMidnight((workStart || '11:00').slice(0, 5));
  let endMin = minutesFromMidnight((workEnd || '21:00').slice(0, 5));
  for (const seg of segments) {
    if (!seg?.start) continue;
    const s = minutesFromMidnight(String(seg.start).slice(0, 5));
    if (!Number.isFinite(s)) continue;
    const d = Number(seg.duration) || 60;
    startMin = Math.min(startMin, s);
    endMin = Math.max(endMin, s + d);
  }
  startMin = Math.max(0, Math.floor(startMin / 60) * 60);
  endMin = Math.min(24 * 60, Math.max(Math.ceil(endMin / 60) * 60, startMin + 60));
  return {
    gridStartMin: startMin,
    gridEndMin: endMin,
    gridHours: Math.ceil((endMin - startMin) / 60),
  };
}

export function timeFromOccurrenceStart(iso) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TEACHER_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const hour = parts.find((p) => p.type === 'hour')?.value || '00';
  const minute = parts.find((p) => p.type === 'minute')?.value || '00';
  return `${hour}:${minute}`;
}

export function occurrenceDateMoscow(iso) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TEACHER_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || TEACHER_TZ;
  } catch {
    return TEACHER_TZ;
  }
}

export function loadViewerTzPreference() {
  try {
    const raw = localStorage.getItem(VIEWER_TZ_STORAGE_KEY);
    if (!raw) return TEACHER_TZ;
    if (raw === VIEWER_TZ_BROWSER) return VIEWER_TZ_BROWSER;
    if (VIEWER_TZ_OPTIONS.some((o) => o.value === raw)) return raw;
  } catch {
    /* ignore */
  }
  return TEACHER_TZ;
}

export function saveViewerTzPreference(value) {
  try {
    localStorage.setItem(VIEWER_TZ_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

/** Резолвит preference → IANA timezone. */
export function resolveViewerTimezone(preference) {
  if (!preference || preference === TEACHER_TZ) return TEACHER_TZ;
  if (preference === VIEWER_TZ_BROWSER) return getBrowserTimezone();
  return preference;
}

export function timezoneShortLabel(ianaTz) {
  if (!ianaTz || ianaTz === TEACHER_TZ) return TEACHER_TZ_SHORT;
  const known = VIEWER_TZ_OPTIONS.find((o) => o.value === ianaTz);
  if (known?.short) return known.short;
  return formatMskOffset(ianaTz);
}

function formatPartsInTz(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  const weekdayEn = get('weekday'); // Mon, Tue...
  const wdMap = { Mon: 'пн', Tue: 'вт', Wed: 'ср', Thu: 'чт', Fri: 'пт', Sat: 'сб', Sun: 'вс' };
  return {
    hour: get('hour') || '00',
    minute: get('minute') || '00',
    dateISO: `${get('year')}-${get('month')}-${get('day')}`,
    weekdayShort: wdMap[weekdayEn] || '',
  };
}

export function formatTimeInTz(dateOrIso, timeZone) {
  const d = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso);
  const p = formatPartsInTz(d, timeZone);
  return `${p.hour}:${p.minute}`;
}

/**
 * Двойная подпись: «16:00» или «16:00 МСК · 18:00» / «16:00 МСК · 00:00 (чт)».
 * @param {string|Date} isoOrDate
 * @param {string} viewerTzPreference — raw preference (может быть 'browser')
 */
export function formatDualTime(isoOrDate, viewerTzPreference) {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  const msk = formatPartsInTz(d, TEACHER_TZ);
  const mskTime = `${msk.hour}:${msk.minute}`;
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  if (viewerTz === TEACHER_TZ) return mskTime;

  const local = formatPartsInTz(d, viewerTz);
  const localTime = `${local.hour}:${local.minute}`;
  const dayShift = local.dateISO !== msk.dateISO;
  const short = timezoneShortLabel(viewerTz);
  if (dayShift && local.weekdayShort) {
    return `${mskTime} ${TEACHER_TZ_SHORT} · ${localTime} (${local.weekdayShort})`;
  }
  return `${mskTime} ${TEACHER_TZ_SHORT} · ${localTime}`;
}

/**
 * Для шаблонного HH:MM в weekday (0=пн): якорим на ближайший такой день в МСК.
 */
export function formatDualTimeFromTemplate(hhmm, weekday, viewerTzPreference) {
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  const time = (hhmm || '00:00').slice(0, 5);
  if (viewerTz === TEACHER_TZ) return time;

  const todayIso = moscowDateISO();
  const today = parseISODate(todayIso);
  const todayWd = (today.getDay() + 6) % 7;
  const delta = (weekday - todayWd + 7) % 7;
  const targetIso = addDaysISO(todayIso, delta);
  return formatDualTimeOnDate(targetIso, time, viewerTzPreference);
}

/** HH:MM на конкретной дате (МСК) → dual подпись. */
export function formatDualTimeOnDate(isoDate, hhmm, viewerTzPreference) {
  const time = (hhmm || '00:00').slice(0, 5);
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  if (viewerTz === TEACHER_TZ) return time;
  const [y, m, day] = String(isoDate).split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  // Europe/Moscow = UTC+3 constantly
  const utcMs = Date.UTC(y, m - 1, day, h - 3, mi, 0);
  return formatDualTime(new Date(utcMs), viewerTzPreference);
}

/** Текст часов в тулбаре. */
export function formatNowClockLabel(now = new Date(), viewerTzPreference = TEACHER_TZ) {
  const msk = formatTimeInTz(now, TEACHER_TZ);
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  if (viewerTz === TEACHER_TZ) {
    return `Сейчас ${msk} ${TEACHER_TZ_SHORT}`;
  }
  const local = formatTimeInTz(now, viewerTz);
  const short = timezoneShortLabel(viewerTz);
  return `Сейчас ${msk} ${TEACHER_TZ_SHORT} · ${local} ${short}`;
}

/**
 * Позиция линии «сейчас» в пикселях от верха колонки (МСК).
 * null если вне рабочего окна или сегодня не в отображаемой неделе.
 */
export function nowLineTopPx({
  now = new Date(),
  workStart,
  workEnd,
  hourHeight,
  todayIso = null,
}) {
  const today = todayIso || moscowDateISO(now);
  if (moscowDateISO(now) !== today) return null;

  const nowMin = minutesFromMidnight(formatTimeInTz(now, TEACHER_TZ));
  const startMin = minutesFromMidnight((workStart || '11:00').slice(0, 5));
  const endMin = minutesFromMidnight((workEnd || '21:00').slice(0, 5));
  if (nowMin < startMin || nowMin > endMin) return null;
  return ((nowMin - startMin) / 60) * hourHeight;
}
